import React, { useEffect, useState } from 'react';
import { Popup } from '../common/Popup.jsx';
import useSellerBudget from '../../hooks/useSellerBudget.js';
import NotificationService from '../../services/notificationService.js';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { BID_STEP, computeCounterOfferLimits } from '../../utils/bidValidation.js';

const formatPrice = (amount) => {
  if (amount == null) return 'N/A';
  if (amount >= 1000000) {
    return `€${(amount / 1000000).toFixed(1)}M`;
  }
  return `€${amount.toLocaleString()}`;
};

const getIdString = (value) => {
  const id = value?._id || value;
  return id ? id.toString() : null;
};

/**
 * Popup to send a counter bid in response to an incoming offer.
 *
 * Props:
 *  - bidId: Mongo ID of the bid being countered
 *  - isOpen: boolean
 *  - onClose: function
 *  - onSuccess: function(responseBid) – called after successful counter submission
 *  - leagueId: optional league id from the notification being handled
 */
// `initialAmount` is optional and can be provided by the parent component (e.g. from
// the notification payload) so that we can immediately show a sensible default
// in the input while we are still fetching the full bid details from the API.

function CounterBidPopup({ bidId, isOpen, onClose, onSuccess, initialAmount = null, leagueId = null }) {
  const [bidDetails, setBidDetails] = useState(null);
  const [playerInfo, setPlayerInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [amountInput, setAmountInput] = useState(''); // raw euros (string)
  const resolvedLeagueId = leagueId ? leagueId.toString() : null;
  const requestedBidId = bidId ? bidId.toString() : null;
  const loadedBidId = getIdString(bidDetails?._id);
  const hasCurrentBidDetails = Boolean(bidDetails && requestedBidId && loadedBidId === requestedBidId);
  const currentBidDetails = hasCurrentBidDetails ? bidDetails : null;
  const currentPlayerInfo = hasCurrentBidDetails ? playerInfo : null;
  const parsedInitialAmount = Number(initialAmount);
  const initialAmountInput = initialAmount != null && Number.isFinite(parsedInitialAmount)
    ? parsedInitialAmount.toString()
    : '';
  const {
    remainingBudget,
    teamId,
    loading: budgetLoading,
    error: budgetError
  } = useSellerBudget(bidId, resolvedLeagueId);

  const biddingTeamId = getIdString(currentBidDetails?.biddingTeam);
  const sellingTeamId = getIdString(currentBidDetails?.player?.currentTeam);
  const viewerTeamId = getIdString(teamId);
  const isFreeAgent = currentBidDetails ? !sellingTeamId : false;
  const isBuyerCounter = Boolean(viewerTeamId && biddingTeamId && viewerTeamId === biddingTeamId);
  const isSellerCounter = Boolean(viewerTeamId && sellingTeamId && viewerTeamId === sellingTeamId);
  const counterRoleKnown = Boolean(!currentBidDetails || isFreeAgent || isBuyerCounter || isSellerCounter);
  const shouldCheckBudget = Boolean(isFreeAgent || isBuyerCounter);
  const counterLimits = currentBidDetails
    ? computeCounterOfferLimits({
      isFreeAgent,
      marketValue: currentPlayerInfo?.marketValue,
      currentAmount: currentBidDetails.amount,
      isBuyer: isBuyerCounter,
      reservedAmount: currentBidDetails.reservedAmount,
      remainingBudget: shouldCheckBudget ? remainingBudget : null
    })
    : { min: 1, max: null, direction: 'higher' };
  const minAmount = counterLimits.min;
  const maxAmount = counterLimits.max;

  // Reset stale bid state before each load so a previous bid cannot be submitted
  // for the newly requested bid if this request fails or returns late.
  useEffect(() => {
    if (!isOpen || !requestedBidId) {
      setBidDetails(null);
      setPlayerInfo(null);
      setAmountInput('');
      setError(null);
      setLoading(false);
      return;
    }

    if (!resolvedLeagueId) {
      setBidDetails(null);
      setPlayerInfo(null);
      setAmountInput('');
      setError('Missing league information for this bid. Refresh notifications and try again.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    async function fetchBid() {
      setBidDetails(null);
      setPlayerInfo(null);
      setAmountInput(initialAmountInput);
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/transfers/bids/${requestedBidId}`, {
          params: resolvedLeagueId ? { leagueId: resolvedLeagueId } : {}
        });
        const loadedBid = res?.bid;
        if (!loadedBid) {
          throw new Error('Bid details response did not include bid data');
        }
        if (getIdString(loadedBid._id) !== requestedBidId) {
          throw new Error('Loaded bid did not match requested bid');
        }

        if (!cancelled) {
          setBidDetails(loadedBid);
          setPlayerInfo(res.player);
          // Default input value to the current bid **only** if we haven't
          // already set a value through `initialAmount` or user input. This
          // prevents us from clobbering a value the user might have already
          // typed while the request was in flight.
          setAmountInput((prev) => prev === '' ? loadedBid.amount.toString() : prev);
        }
      } catch (err) {
        if (!cancelled) {
          setBidDetails(null);
          setPlayerInfo(null);
          setAmountInput('');
          setError(err.response?.data?.error || err.message || 'Failed to load bid');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    fetchBid();
    return () => { cancelled = true; };
  }, [isOpen, requestedBidId, resolvedLeagueId, initialAmountInput]);

  // Helper to validate current input
  const amountNumeric = Number(amountInput);
  const amountValid = Boolean(currentBidDetails)
    && !error
    && counterRoleKnown
    && Number.isFinite(amountNumeric)
    && amountNumeric >= minAmount
    && (maxAmount == null || amountNumeric <= maxAmount);

  const handleSubmit = async () => {
    if (!amountValid || !resolvedLeagueId) return;
    const toastId = toast.loading('Sending counter offer...');
    try {
      const res = await NotificationService.respondToBid(bidId, 'counter', amountNumeric, resolvedLeagueId);
      toast.update(toastId, { render: 'Counter offer sent!', type: 'success', isLoading: false, autoClose: 3000 });
      if (onSuccess) onSuccess(res);
      onClose();
    } catch (err) {
      toast.update(toastId, { render: err.response?.data?.error || err.message || 'Failed to send counter', type: 'error', isLoading: false, autoClose: 5000 });
    }
  };

  if (!isOpen) return null;

  return (
    <Popup popOpen={isOpen} close={onClose}>
      <h3>COUNTER BID</h3>

      {loading && <div>Loading bid details...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {currentBidDetails && (
        <div className="bid-details">
          <p><strong>Player:</strong> {currentBidDetails.player?.name}</p>
          <p><strong>Current Offer:</strong> {formatPrice(currentBidDetails.amount)}</p>
          <p><strong>Market Value:</strong> {formatPrice(currentPlayerInfo?.marketValue)}</p>
        </div>
      )}

      {budgetError && <div style={{ color: 'red' }}>{budgetError}</div>}
      {budgetLoading && <div>Loading budget...</div>}
      {remainingBudget != null && shouldCheckBudget && (
        <p><strong>Your Remaining Budget:</strong> {formatPrice(remainingBudget)}</p>
      )}

      {/* Input */}
      <div className="form-group" style={{ marginTop: '12px' }}>
        <label htmlFor="counterAmount">Counter Amount (€)</label>
        <input
          id="counterAmount"
          type="number"
          min={minAmount}
          max={maxAmount ?? undefined}
          step={BID_STEP}
          value={amountInput}
          onChange={(e) => setAmountInput(e.target.value)}
        />
        <small className="helper-text">
          Minimum {formatPrice(minAmount)}{maxAmount != null ? `, Maximum ${formatPrice(maxAmount)}` : ''}
        </small>
      </div>

      <div className="buttons-block" style={{ marginTop: '18px' }}>
        <button type="button" className="submit-btn trans-btn white" onClick={onClose}>
          CANCEL
        </button>
        <button
          type="button"
          className="submit-btn submit_offer_btn"
          onClick={handleSubmit}
          disabled={!amountValid || loading || budgetLoading}
        >
          SEND OFFER
        </button>
      </div>
    </Popup>
  );
}

export default CounterBidPopup;
